import React, { useEffect, useRef, useState } from "react";
import { FiPlayCircle, FiX, FiExternalLink, FiFileText } from "react-icons/fi";
import { TUTORIALS, tutorialEmbedUrl } from "../constants/tutorials";

/** Popup YouTube player; closes on Escape or a backdrop click. */
export const TutorialVideoModal = ({ tutorial, onClose }) => {
  useEffect(() => {
    if (!tutorial) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [tutorial, onClose]);

  if (!tutorial) return null;

  return (
    <div
      className="tutorial-modal"
      role="dialog"
      aria-modal="true"
      aria-label={tutorial.title}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="tutorial-modal__panel">
        <div className="tutorial-modal__header">
          <h2 className="tutorial-modal__title">{tutorial.title}</h2>
          <a
            className="tutorial-modal__link"
            href={tutorial.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiExternalLink aria-hidden="true" />
            Open on YouTube
          </a>
          <button
            type="button"
            className="tutorial-modal__close"
            onClick={onClose}
            aria-label="Close tutorial"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>
        <div className="tutorial-modal__frame">
          <iframe
            src={tutorialEmbedUrl(tutorial)}
            title={tutorial.title}
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};

/** Top-right "Tutorials available" badge with a popover listing every tutorial. */
export const TutorialsBadge = () => {
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!TUTORIALS.length) return null;

  return (
    <div className="tutorials-badge" ref={wrapRef}>
      <button
        type="button"
        className="tutorials-badge__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="tutorials-popover"
      >
        <span className="tutorials-badge__pulse" aria-hidden="true" />
        <FiPlayCircle aria-hidden="true" />
        Tutorials available
        <span className="tutorials-badge__count">{TUTORIALS.length}</span>
      </button>

      {open && (
        <div id="tutorials-popover" className="tutorials-badge__popover">
          <h3 className="tutorials-badge__heading">Munshot Tutorials</h3>
          <ol className="tutorials-badge__list">
            {TUTORIALS.map((tutorial, index) => (
              <li key={tutorial.id}>
                <button
                  type="button"
                  className="tutorials-badge__item"
                  onClick={() => {
                    setOpen(false);
                    setPlaying(tutorial);
                  }}
                >
                  <span className="tutorials-badge__num">{index + 1}.</span>
                  <span className="tutorials-badge__item-title">{tutorial.title}</span>
                  <FiPlayCircle aria-hidden="true" className="tutorials-badge__play" />
                </button>
              </li>
            ))}
          </ol>
        </div>
      )}

      <TutorialVideoModal tutorial={playing} onClose={() => setPlaying(null)} />
    </div>
  );
};

/** Callout beside a module action that points at it and plays its tutorial. */
export const TutorialCallout = ({ tutorial, onPlay, label = "Watch tutorial" }) => (
  <button
    type="button"
    className="tutorial-callout"
    onClick={() => onPlay(tutorial)}
    aria-label={`Watch tutorial: ${tutorial.title}`}
    title={tutorial.title}
  >
    <span className="tutorial-callout__pulse" aria-hidden="true" />
    <FiPlayCircle aria-hidden="true" />
    {label}
  </button>
);

/** Callout beside a module action that opens its API docs section. */
export const DocLinkCallout = ({ docLink, onOpen }) => (
  <button
    type="button"
    className="tutorial-callout"
    onClick={() => onOpen(docLink)}
    aria-label={`Open docs: ${docLink.title}`}
    title={docLink.title}
  >
    <span className="tutorial-callout__pulse" aria-hidden="true" />
    <FiFileText aria-hidden="true" />
    {docLink.label}
  </button>
);
